import React, { useEffect } from 'react';
import { Calendar, Mail, MessageCircle, ArrowRight, Users, BookOpen, MessageSquare, AlertCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Button from '../components/common/Button';
import GradientLogo from '../components/common/GradientLogo';
import SurveyForm from '../components/SurveyForm';
import { WHATSAPP_GROUP_LINK, EXTERNAL_LINKS } from '../config/constants';
import { decryptData } from '../utils/crypto';

const ThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const encryptedData = searchParams.get('data');
  
  let email: string | null = null;
  let lancamento: number = 1;

  try {
    if (encryptedData) {
      const decryptedData = decryptData(encryptedData);
      email = decryptedData.email;
      lancamento = decryptedData.lancamento;
    }
  } catch (error) {
    console.error('Error decrypting data:', error);
    // Em caso de erro na descriptografia, redireciona para a página inicial
    navigate('/inscricao', { replace: true });
  }

  // Efeito para scroll inicial
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Efeito para validação e redirecionamento
  useEffect(() => {
    if (!email && location.pathname === '/obrigado') {
      navigate('/inscricao', { replace: true });
    }
  }, [email, navigate, location.pathname]);

  if (!email) {
    return null;
  }

  const handleJoinDiscord = () => {
    window.open(WHATSAPP_GROUP_LINK, '_blank');
  };

  const handleAddToCalendar = () => {
    // Google Calendar link with event details
    const eventDetails = {
      text: 'Java Dev Week - Aula 1',
      dates: '20250609T230000Z/20250610T000000Z',
      details: 'Primeira aula da Java Dev Week - Fundamentos do Java',
      location: 'Online'
    };

    const googleCalendarUrl = `${EXTERNAL_LINKS.GOOGLE_CALENDAR_BASE}&text=${encodeURIComponent(eventDetails.text)}&dates=${eventDetails.dates}&details=${encodeURIComponent(eventDetails.details)}&location=${encodeURIComponent(eventDetails.location)}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  return (
    <div className="font-inter text-[#0d0d0d] bg-[#f2f0f0]">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes progressAnimation {
            from { width: 0%; }
            to { width: 90%; }
          }
        `
      }} />

      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#0d0d0d] to-[#1a1a1a] text-white py-16 md:py-20">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-12 bg-gradient-to-r from-[#ffffff] to-[#f20587] text-transparent bg-clip-text animate-gradient">
                Parabéns! Falta Pouco Para Completar Sua Inscrição!
              </h1>

              {/* Progress Bar */}
              <div className="max-w-xl mx-auto mb-16">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-3xl font-bold text-[#ffffff]">90%</span>
                </div>
                <div className="w-full bg-white/5 rounded-lg h-8 p-1">
                  <div 
                    className="bg-[#05f217] h-full rounded-md transition-all duration-1000 ease-out"
                    style={{
                      animation: 'progressAnimation 2s ease-out forwards'
                    }}
                  />
                </div>
              </div>

              <p className="text-xl md:text-2xl mb-8 text-center max-w-3xl mx-auto font-medium leading-relaxed bg-gradient-to-r from-white to-white/90 bg-clip-text">
                Falta apenas um passo importante para maximizar sua experiência na Java Dev Week!
              </p>

              {/* Survey Form */}
              <SurveyForm email={email || ''} lancamento={lancamento} />

              <div className="bg-white/10 backdrop-blur p-6 rounded-lg border border-white/20 mt-12">
                <h2 className="text-2xl font-bold mb-4">Por que entrar no grupo ?</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <Users className="w-12 h-12 text-[#f2055c] mx-auto mb-3" />
                    <h3 className="font-bold mb-2">Networking</h3>
                    <p className="text-sm opacity-80">Conecte-se com outros devs Java</p>
                  </div>
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 text-[#f2055c] mx-auto mb-3" />
                    <h3 className="font-bold mb-2">Suporte</h3>
                    <p className="text-sm opacity-80">Tire dúvidas em tempo real</p>
                  </div>
                  <div className="text-center">
                    <BookOpen className="w-12 h-12 text-[#f2055c] mx-auto mb-3" />
                    <h3 className="font-bold mb-2">Conteúdo Extra</h3>
                    <p className="text-sm opacity-80">Acesse materiais exclusivos</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 mt-8">
                <Button onClick={handleJoinDiscord} size="large">
                  <span className="flex items-center gap-2">
                    Entrar no Grupo Agora
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </Button>
                <Button onClick={handleAddToCalendar} variant="secondary" size="large">
                  <span className="flex items-center gap-2">
                    Adicionar ao Calendário
                    <Calendar className="w-5 h-5" />
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Schedule Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Prepare-se para a Semana
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Confira o cronograma completo e não perca nenhuma aula
              </p>
              <Button onClick={handleJoinDiscord} size="large">
                <span className="flex items-center gap-2">
                  Garantir Acesso ao Grupo
                  <ArrowRight className="w-5 h-5" />
                </span>
              </Button>
            </div>

            <div className="grid gap-6">
              {[
                {
                  day: "Dia 1",
                  title: "Salários de R$8.000,00 à R$15.000,00 (mesmo em 2025)",
                  time: "20h",
                  description: "Por que Java é a linguagem dos salários de R$8.000,00 à R$15.000,00 (mesmo em 2025)"
                },
                {
                  day: "Dia 2",
                  title: "Sistema que impressiona recrutadores",
                  time: "20h",
                  description: "Como construir seu primeiro sistema com Java que impressiona recrutadores"
                },
                {
                  day: "Dia 3",
                  title: "Escolha sua vaga com esses segredos",
                  time: "20h",
                  description: "Os 3 segredos dos devs que escolhem suas próprias vagas"
                }
              ].map((day, index) => (
                <div 
                  key={index} 
                  className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-[#f2055c] mb-2">{day.day}</h3>
                      <h4 className="text-lg font-bold mb-2">{day.title}</h4>
                      <p className="text-gray-600">{day.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-500">{day.time}</span>
                      <Button onClick={handleAddToCalendar} variant="secondary" size="small">
                        <Calendar className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0d0d0d] to-[#1a1a1a] text-white">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Não perca tempo! Entre no Grupo agora
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Junte-se a centenas de devs que já estão se preparando para a Java Dev Week
            </p>
            <Button onClick={handleJoinDiscord} size="large">
              <span className="flex items-center gap-2">
                Acessar o Grupo
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ThankYouPage;